/*
 * Header dropdown (DEED)
 *
 * Opent het dropdown-menu onder een hoofdmenu-item met sub-items (bijv. "Über uns").
 * De sub-items komen uit het Shopify-menu (Content > Menus): alles wat daar onder het
 * hoofdmenu-item is genest. Markup en styling staan in snippets/header-dropdown.liquid.
 *
 * - Hover: opent na OPEN_DELAY, sluit na CLOSE_DELAY (zelfde idee als het megamenu).
 * - Toetsenbord: focus (Tab) opent, Escape sluit.
 * - Het hoofdmenu-item blijft een gewone link (klik = naar de pagina). Alleen als de
 *   link "#" is, opent een klik het dropdown-menu in plaats van te navigeren.
 */
const SELECTOR = '[data-header-dropdown]';
const OPEN_CLASS = 'is-open';
const OPEN_DELAY = 150;
const CLOSE_DELAY = 200;
const VIEWPORT_MARGIN = 16;

/** @type {WeakMap<Element, { open?: ReturnType<typeof setTimeout>, close?: ReturnType<typeof setTimeout> }>} */
const timers = new WeakMap();

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function findDropdown(target) {
  return target instanceof Element ? target.closest(SELECTOR) : null;
}

/** @param {Element} dropdown */
function clearTimers(dropdown) {
  const entry = timers.get(dropdown);
  if (!entry) return;
  clearTimeout(entry.open);
  clearTimeout(entry.close);
  timers.delete(dropdown);
}

/** @param {Element} dropdown */
function isOpen(dropdown) {
  return dropdown.classList.contains(OPEN_CLASS);
}

/**
 * Lines the panel up with the text of the menu item and keeps it inside the viewport.
 * @param {HTMLElement} dropdown
 */
function position(dropdown) {
  const panel = /** @type {HTMLElement | null} */ (dropdown.querySelector('[data-header-dropdown-panel]'));
  const toggle = dropdown.querySelector('[data-header-dropdown-toggle]');
  if (!panel || !toggle) return;

  const title = toggle.querySelector('.link-underline-text') ?? toggle;

  panel.style.setProperty('--header-dropdown-left', '0px');

  const panelRect = panel.getBoundingClientRect();
  const titleRect = title.getBoundingClientRect();
  const paddingLeft = parseFloat(getComputedStyle(panel).paddingLeft) || 0;
  const viewportWidth = document.documentElement.clientWidth;

  let left = titleRect.left - paddingLeft;
  left = Math.min(left, viewportWidth - VIEWPORT_MARGIN - panelRect.width);
  left = Math.max(left, VIEWPORT_MARGIN);

  panel.style.setProperty('--header-dropdown-left', `${Math.round(left - panelRect.left)}px`);
}

/** @param {HTMLElement} dropdown */
function open(dropdown) {
  clearTimers(dropdown);
  if (isOpen(dropdown)) return;

  // Inside the "More" overflow menu there is no room for a dropdown
  if (dropdown.getAttribute('slot') === 'overflow') return;

  closeAll(dropdown);

  document.querySelectorAll('header-megamenu.active').forEach((megamenu) => {
    /** @type {any} */ (megamenu).forceClose?.();
  });

  position(dropdown);
  dropdown.querySelector('[data-header-dropdown-panel]')?.removeAttribute('inert');
  dropdown.classList.add(OPEN_CLASS);
  dropdown.querySelector('[data-header-dropdown-toggle]')?.setAttribute('aria-expanded', 'true');
}

/** @param {Element} dropdown */
function close(dropdown) {
  clearTimers(dropdown);
  if (!isOpen(dropdown)) return;

  dropdown.classList.remove(OPEN_CLASS);
  dropdown.querySelector('[data-header-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
  // The panel fades out for a moment: take it out of reach right away (no focus, no hover)
  dropdown.querySelector('[data-header-dropdown-panel]')?.setAttribute('inert', '');
}

/** @param {Element} [except] */
function closeAll(except) {
  document.querySelectorAll(`${SELECTOR}.${OPEN_CLASS}`).forEach((dropdown) => {
    if (dropdown !== except) close(dropdown);
  });
}

/** @param {HTMLElement} dropdown */
function scheduleOpen(dropdown) {
  const entry = timers.get(dropdown) ?? {};
  clearTimeout(entry.close);
  entry.close = undefined;

  if (!isOpen(dropdown) && entry.open === undefined) {
    entry.open = setTimeout(() => open(dropdown), OPEN_DELAY);
  }

  timers.set(dropdown, entry);
}

/** @param {HTMLElement} dropdown */
function scheduleClose(dropdown) {
  const entry = timers.get(dropdown) ?? {};
  clearTimeout(entry.open);
  clearTimeout(entry.close);
  entry.open = undefined;

  entry.close = setTimeout(() => {
    // Still in use (pointer came back, or keyboard focus is inside): keep it open
    if (dropdown.matches(':hover') || dropdown.querySelector(':focus-visible')) {
      timers.delete(dropdown);
      return;
    }
    close(dropdown);
  }, CLOSE_DELAY);

  timers.set(dropdown, entry);
}

document.addEventListener('mouseover', (event) => {
  const dropdown = findDropdown(event.target);

  if (dropdown) {
    scheduleOpen(dropdown);
    return;
  }

  // Moving on to another main menu item (Winter, Sommer, ...) closes the dropdown right away
  if (event.target instanceof Element && event.target.closest('.menu-list__list-item')) {
    closeAll();
  }
});

document.addEventListener('mouseout', (event) => {
  const dropdown = findDropdown(event.target);
  if (!dropdown) return;

  const next = event.relatedTarget;
  if (next instanceof Node && dropdown.contains(next)) return;

  scheduleClose(dropdown);
});

document.addEventListener('focusin', (event) => {
  const dropdown = findDropdown(event.target);

  if (!dropdown) {
    closeAll();
    return;
  }

  // Keyboard focus opens the dropdown. Focus from a mouse click does not: hover already takes care of that
  if (event.target instanceof Element && event.target.matches(':focus-visible')) {
    open(dropdown);
  }
});

document.addEventListener('focusout', (event) => {
  const dropdown = findDropdown(event.target);
  if (!dropdown) return;

  const next = event.relatedTarget;
  if (next instanceof Node && dropdown.contains(next)) return;

  scheduleClose(dropdown);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  document.querySelectorAll(`${SELECTOR}.${OPEN_CLASS}`).forEach((dropdown) => {
    const toggle = /** @type {HTMLElement | null} */ (dropdown.querySelector('[data-header-dropdown-toggle]'));
    const focusWasInPanel = dropdown.contains(document.activeElement) && document.activeElement !== toggle;

    // Move focus back to the menu item first (that re-opens via focusin), then close
    if (focusWasInPanel) toggle?.focus();
    close(dropdown);
  });
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const toggle = event.target.closest('[data-header-dropdown-toggle]');
  const dropdown = findDropdown(toggle);
  if (!toggle || !dropdown) return;

  // A menu item without its own page ("#") opens the dropdown instead of navigating
  const href = toggle.getAttribute('href');
  if (href && href !== '#') return;

  event.preventDefault();
  open(dropdown);
});

window.addEventListener('resize', () => closeAll());
